"use client";

import { useApp } from "./AppProvider";
import type { HeroData } from "@/lib/cms";

interface HeroProps {
  data: HeroData;
}

export default function Hero({ data }: HeroProps) {
  const { isRTL } = useApp();

  const h = (en: string, ar: string) => isRTL ? (ar || en) : en;

  return (
    <section id="home" style={{ minHeight: "100vh", display: "flex", alignItems: "center", position: "relative", overflow: "hidden", paddingTop: "5rem" }}>
      {/* Background */}
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 80% 70% at 50% -20%, rgba(113, 53, 140, 0.3) 0%, transparent 70%), radial-gradient(ellipse 60% 50% at 80% 80%, rgba(7, 174, 185, 0.15) 0%, transparent 60%), radial-gradient(ellipse 50% 60% at 10% 90%, rgba(227, 1, 125, 0.12) 0%, transparent 60%)" }} />
      <div className="animate-pulse-glow" style={{ position: "absolute", top: "15%", right: "8%", width: 450, height: 450, borderRadius: "50%", background: "radial-gradient(circle, rgba(113, 53, 140, 0.35) 0%, rgba(227,1,125,0.15) 50%, transparent 70%)", filter: "blur(40px)" }} />
      <div className="animate-pulse-glow" style={{ position: "absolute", bottom: "10%", left: "5%", width: 350, height: 350, borderRadius: "50%", background: "radial-gradient(circle, rgba(7, 174, 185, 0.3) 0%, transparent 70%)", filter: "blur(40px)", animationDelay: "1.5s" }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(var(--grid-line) 1px, transparent 1px), linear-gradient(90deg, var(--grid-line) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />

      <div className="container" style={{ position: "relative", zIndex: 1, width: "100%" }}>
        <div style={{ maxWidth: 750 }}>
          <div className="section-badge animate-fade-up"><span>✦</span> {h(data.badge_en, data.badge_ar)}</div>

          <h1 className="animate-slide-left" style={{ fontSize: "clamp(2.2rem, 6vw, 5rem)", fontWeight: 900, lineHeight: 1.08, marginBottom: "1.5rem", letterSpacing: "-0.02em", color: "var(--text-primary)" }}>
            {h(data.headline1_en, data.headline1_ar)}{" "}
            <span className="gradient-text-brand">{h(data.headline2_en, data.headline2_ar)}</span>
            <br />
            {h(data.headline3_en, data.headline3_ar)}
          </h1>

          <p className="animate-fade-up" style={{ fontSize: "1.05rem", color: "var(--text-secondary)", lineHeight: 1.75, marginBottom: "2.5rem", maxWidth: 580, animationDelay: "0.2s" }}>
            {h(data.subtitle_en, data.subtitle_ar)}
          </p>

          <div
            className="animate-fade-up hero-cta-wrap"
            style={{ display: "flex", gap: "1rem", flexWrap: "wrap", animationDelay: "0.35s", flexDirection: isRTL ? "row-reverse" : "row" }}
          >
            <button className="btn-primary">{h(data.cta1_en, data.cta1_ar)}</button>
            <button className="btn-secondary">{h(data.cta2_en, data.cta2_ar)}</button>
          </div>

          {/* Stats */}
          <div
            className="animate-fade-up hero-stats"
            style={{ display: "flex", gap: "3rem", marginTop: "4rem", paddingTop: "2rem", borderTop: "1px solid var(--border-subtle)", animationDelay: "0.5s", flexDirection: isRTL ? "row-reverse" : "row", flexWrap: "wrap" }}
          >
            {data.stats.map((s) => (
              <div key={s.label_en} style={{ textAlign: isRTL ? "right" : "left" }}>
                <div className="stat-number">{s.number}</div>
                <div style={{ fontSize: "0.82rem", color: "var(--text-muted)", marginTop: "0.25rem", fontWeight: 500, letterSpacing: "0.04em" }}>
                  {h(s.label_en, s.label_ar)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Floating card — hidden on mobile via CSS class */}
        <div
          className="animate-float glass-card-strong animate-slide-right hero-floating-card"
          style={{ [isRTL ? "left" : "right"]: 0 }}
        >
          <div style={{ width: 48, height: 48, background: "var(--gradient-brand)", borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.4rem", marginBottom: "1rem", boxShadow: "0 8px 24px rgba(227,1,125,0.4)" }}>🚀</div>
          <h3 style={{ fontWeight: 700, fontSize: "1.05rem", marginBottom: "0.6rem", color: "var(--text-primary)" }}>{h(data.card_title_en, data.card_title_ar)}</h3>
          <p style={{ color: "var(--text-muted)", fontSize: "0.87rem", lineHeight: 1.65 }}>{h(data.card_desc_en, data.card_desc_ar)}</p>
          <div style={{ marginTop: "1.5rem", display: "flex", gap: "0.5rem", flexWrap: "wrap", flexDirection: isRTL ? "row-reverse" : "row" }}>
            {data.card_tags.map((t) => (
              <span key={t.tag} style={{ padding: "0.25rem 0.7rem", borderRadius: "50px", background: "var(--tag-bg)", border: "1px solid var(--tag-border)", color: "var(--color-pink)", fontSize: "0.72rem", fontWeight: 600 }}>
                {t.tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
